import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * TrendIndicator Component
 * 
 * Displays hot/cold/neutral trend badge with percentage and emoji.
 * Three size variants for different contexts.
 * 
 * @param {string} trend - Trend status: "hot" | "cold" | "neutral"
 * @param {number} percentage - Trend percentage (positive or negative)
 * @param {string} size - Display size: "compact" | "standard" | "featured"
 */
const TrendIndicator = ({ trend, percentage, size = 'standard' }) => {
  // Determine emoji and colors based on trend
  const getTrendConfig = () => {
    switch (trend) {
      case 'hot':
        return {
          emoji: '🔥',
          label: 'HOT',
          color: C.error, // red
          backgroundColor: withAlpha(C.error, 0.15),
          glow: withAlpha(C.error, 0.3)
        };
      case 'cold':
        return {
          emoji: '🧊',
          label: 'COLD',
          color: C.info, // blue
          backgroundColor: withAlpha(C.info, 0.15),
          glow: withAlpha(C.info, 0.3)
        };
      case 'neutral':
      default:
        return {
          emoji: '➖',
          label: 'NEUTRAL',
          color: C.coolGray[400], // gray
          backgroundColor: withAlpha(C.coolGray[400], 0.15),
          glow: withAlpha(C.coolGray[400], 0.2)
        };
    }
  };

  const config = getTrendConfig();

  // Size configurations
  const sizeConfig = {
    compact: {
      emojiSize: '14px',
      labelSize: '10px',
      percentageSize: '12px',
      padding: '4px 6px',
      gap: '4px',
      showLabel: false
    },
    standard: {
      emojiSize: '16px',
      labelSize: '11px',
      percentageSize: '14px',
      padding: '6px 10px',
      gap: '6px',
      showLabel: true
    },
    featured: {
      emojiSize: '20px',
      labelSize: '12px',
      percentageSize: '16px',
      padding: '8px 12px',
      gap: '8px',
      showLabel: true
    }
  };

  const sizeStyles = sizeConfig[size] || sizeConfig.standard;

  // Format percentage with + or - sign
  const formattedPercentage = percentage > 0 ? `+${percentage}%` : `${percentage}%`;

  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: sizeStyles.gap,
    padding: sizeStyles.padding,
    borderRadius: '12px',
    backgroundColor: config.backgroundColor,
    border: `1px solid ${config.color}`,
    boxShadow: `0 0 8px ${config.glow}`,
    flexShrink: 0
  };

  const emojiStyle = {
    fontSize: sizeStyles.emojiSize,
    lineHeight: 1
  };

  const labelStyle = {
    fontSize: sizeStyles.labelSize,
    fontWeight: '700',
    color: config.color,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1
  };

  const percentageStyle = {
    fontSize: sizeStyles.percentageSize,
    fontWeight: '600',
    color: config.color,
    lineHeight: 1
  };

  return (
    <div style={containerStyle}>
      <span style={emojiStyle}>{config.emoji}</span>
      {sizeStyles.showLabel && (
        <span style={labelStyle}>{config.label}</span>
      )}
      <span style={percentageStyle}>{formattedPercentage}</span>
    </div>
  );
};

TrendIndicator.propTypes = {
  trend: PropTypes.oneOf(['hot', 'cold', 'neutral']).isRequired,
  percentage: PropTypes.number.isRequired,
  size: PropTypes.oneOf(['compact', 'standard', 'featured'])
};

export default TrendIndicator;
